import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  lookupCustomersByEmail,
  type EmailDedupMatch,
} from '../services/customerService';

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface UseEmailDedupLookupResult {
  /** First (and usually only) matching customer; null when none / not ready. */
  match: EmailDedupMatch | null;
  /** Any additional matches beyond the first — surfaced so the popover can
   *  hint "2 customers share this email" instead of pretending there's one. */
  additional: EmailDedupMatch[];
  isLoading: boolean;
}

/**
 * Debounced live lookup for the Add Customer drawer. Watches the email field
 * and resolves to the existing customer (if any) whose normalized email
 * matches — so staff catch the duplicate before they create a second record.
 *
 * Debounce (300ms) and query-cache key on the *debounced* value, not the raw
 * typed value — so React Query dedupes across keystrokes and we don't fire a
 * request per character.
 */
export function useEmailDedupLookup(
  email: string,
  options: { enabled?: boolean } = {}
): UseEmailDedupLookupResult {
  const { enabled = true } = options;
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    if (!enabled) {
      setDebounced('');
      return;
    }
    const trimmed = email.trim();
    if (!EMAIL_SHAPE.test(trimmed)) {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(trimmed), 300);
    return () => clearTimeout(timer);
  }, [email, enabled]);

  const { data, isFetching } = useQuery({
    queryKey: ['customer-email-dedup', debounced.toLowerCase()],
    queryFn: () => lookupCustomersByEmail(debounced),
    enabled: enabled && debounced.length > 0,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 0,
  });

  const matches = data || [];
  return {
    match: matches[0] || null,
    additional: matches.slice(1),
    isLoading: isFetching,
  };
}
